import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import CategoryButton from "../components/CategoryButton";
import ProductCard from "../components/ProductCard";
import { getCategories, getProducts } from "../utils/api";

function normCat(c) {
  return {
    id: c?.id,
    label: c?.title ?? c?.label ?? "Category",
    image_path: c?.image_path ?? c?.image ?? "",
  };
}

export default function Shop({ searchQuery, setSearchQuery, currentUser }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCat = params.get("cat");

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [activeCatId, setActiveCatId] = useState(initialCat || "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActiveCatId(initialCat || "all");
  }, [initialCat]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const [cats, prods] = await Promise.all([
          getCategories(),
          getProducts(),
        ]);
        if (cancelled) return;

        setCategories(Array.isArray(cats) ? cats.map(normCat) : []);
        setProducts(Array.isArray(prods) ? prods : []);
      } catch {
        if (!cancelled) {
          setCategories([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function setCat(val) {
    const v = String(val || "all");
    setActiveCatId(v);
    navigate(v === "all" ? "/categories" : `/categories?cat=${v}`);
  }

  const filtered = useMemo(() => {
    let list = Array.isArray(products) ? products : [];

    if (activeCatId && activeCatId !== "all") {
      const target = String(activeCatId);
      list = list.filter(
        (p) =>
          String(
            p?.product_category_id ?? p?.category_id ?? p?.categoryId ?? "",
          ) === target,
      );
    }

    const q = String(searchQuery || "")
      .trim()
      .toLowerCase();
    if (q) {
      list = list.filter((p) =>
        String(p?.name || "")
          .toLowerCase()
          .includes(q),
      );
    }

    return list;
  }, [products, activeCatId, searchQuery]);

  const activeCatLabel = useMemo(() => {
    if (activeCatId === "all") return "All items";
    const found = categories.find((c) => String(c.id) === String(activeCatId));
    return found?.label || "Items";
  }, [activeCatId, categories]);

  return (
    <div className="container pagePadBottom shopPage">
      <div className="shopHeader">
        <div className="shopHeadLeft">
          <div className="shopTitle">Shop</div>
          <div className="shopSubtitle">
            Browse by category, then search for what you need.
          </div>
        </div>

        <div className="shopHeadRight">
          <span className="shopCount">{filtered.length} items</span>
        </div>
      </div>

      <div className="shopControls">
        {typeof setSearchQuery === "function" ? (
          <div className="shopControl shopControlSearch">
            <div className="label">Search</div>
            <div className="shopSearchWrap">
              <input
                className="input shopSearch"
                placeholder="Search items…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {String(searchQuery || "").trim() ? (
                <button
                  type="button"
                  className="shopClearBtn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="shopControl shopControlSelect">
          <div className="label">Category</div>
          <select
            className="input shopSelect"
            value={activeCatId}
            onChange={(e) => setCat(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="shopPills">
        <div className="catRow">
          <Link
            className={`catBtn ${activeCatId === "all" ? "catBtnActive" : ""}`}
            to="/categories"
            onClick={(e) => {
              e.preventDefault();
              setCat("all");
            }}
            title="All categories"
          >
            <span className="catDot" aria-hidden="true" />
            <span className="catBtnLabel">All</span>
          </Link>

          {categories.map((c) => (
            <CategoryButton
              key={c.id}
              cat={c}
              to={`/categories?cat=${c.id}`}
              active={String(activeCatId) === String(c.id)}
              onClick={() => setCat(String(c.id))}
            />
          ))}
        </div>
      </div>

      <div className="shopSectionBar">
        <div className="shopSectionTitle">{activeCatLabel}</div>
        <div className="muted small">Tap an item to view details.</div>
      </div>

      {loading ? (
        <div className="shopHint">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="emptyBox">
          <div className="emptyTitle">No items found</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Try changing category or search keywords.
          </div>
        </div>
      ) : (
        <div className="grid3 shopGrid">
          {filtered.map((p) => (
            <ProductCard key={p.id} item={p} />
          ))}
        </div>
      )}
    </div>
  );
}
